import React from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import chroma from 'chroma-js';
import Helmet from 'react-helmet';

import screenTypes from './screens';

import styles from './questionslist.module.css';

smoothscroll.polyfill();
export interface Option {
    id: number;
    text: string;
    order: number;
    created_at: Date;
    updated_at?: Date;
}

export interface FlowRule {
    values: string[];
    operator: 'AND' | 'OR';
    jump_to: number;
}

export interface Question {
    id: number;
    survey_id: number;
    order: number;
    type: string;
    subtype?: string;
    created_at: Date;
    updated_at?: Date;
    title: string;
    description?: string;
    min_value?: number;
    max_value?: number;
    open_option: boolean;
    required: boolean;
    randomize_options: boolean;
    image?: string;
    hide_result?: boolean;
    sensible_data: boolean;
    extended_props?: any;
    options?: Option[];
    flows?: FlowRule[];
    note?: string;
    children?: Question[];
}

interface QuestionsListProps {
    questions: Question[];
    survey: any;
    mode: 'FillOut' | 'Static' | 'Preview';
    lazyRender?: boolean;
    onQuestionSubmit?: (
        questionId: number,
        response: IResponse
    ) => Promise<boolean>;
    onSurveyFinished?: () => void;
}

export interface IResponse {
    value?: number;
    text?: string;
    options?: number[];
    time?: number;
    meta?: any;
}

export interface IResponses {
    [questionId: number]: IResponse;
}

interface QuestionsListState {
    currentScreenIndex: number;
    screensToDraw: Question[];
    answeredQuestions: number[];
    responses: IResponses;
}

class QuestionsList extends React.Component<
    QuestionsListProps,
    QuestionsListState
> {
    themeStyle: any = null;

    state: QuestionsListState = {
        currentScreenIndex: 0,
        screensToDraw: [],
        answeredQuestions: [],
        responses: {},
    };

    init = () => {
        const { lazyRender, questions, survey } = this.props;

        const params = new URLSearchParams(window.location.search);
        const jumpWelcome =
            params.get('nw') === '1' &&
            questions &&
            questions[0].type === 'Note';

        const screensToDraw =
            lazyRender && questions && questions.length > 0
                ? [questions[jumpWelcome ? 1 : 0]]
                : jumpWelcome
                ? questions.splice(1)
                : questions;
        this.setState({
            screensToDraw,
            currentScreenIndex: jumpWelcome ? 1 : 0,
            answeredQuestions: [],
        });

        const theme = survey
            ? { ...survey.theme, ...(survey.config || {}).theme }
            : null;

        let style: any = {};
        if (!this.themeStyle && theme) {
            style['--font-color'] = theme.font_color || '#111111';
            style['--font-contrast-color'] =
                chroma(style['--font-color']).get('lab.l') < 50
                    ? 'white'
                    : 'black';
            style['--alt-font-color'] =
                theme.alternate_font_color || style['--font-color'];
            style['--primary-color'] = theme.primary_color || '#00BCD4';
            style['--font-color-opacity'] = chroma(style['--font-color']).alpha(
                0.3
            );
            style['--options-bg-color'] =
                theme.options_background_color || style['--font-color-opacity'];
            style['--font'] = theme.font || 'inherit';
            this.themeStyle = style;

            if (theme.custom_css) {
                const styleElement = document.createElement('style');
                styleElement.textContent = theme.custom_css;
                document
                    .getElementsByTagName('head')[0]
                    .appendChild(styleElement);
            }
        }
    };

    componentDidMount() {
        this.init();
        if (this.props.mode !== 'Static') {
            window.addEventListener('resize', this.scrollEvent);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.scrollEvent);
    }

    componentDidUpdate(
        prevProps: QuestionsListProps,
        prevState: QuestionsListState
    ) {
        if (
            this.props.mode !== 'Static' &&
            prevState.currentScreenIndex !== this.state.currentScreenIndex
        ) {
            this.scrollToCurrentScreen();
        }
        if (this.props.questions !== prevProps.questions) this.init();
    }

    onQuestionSubmit = async (response: IResponse) => {
        const currentScreen =
            this.props.questions[this.state.currentScreenIndex];
        let moveNext = true;

        if (this.props.onQuestionSubmit) {
            moveNext = await this.props.onQuestionSubmit(
                currentScreen.id,
                response
            );
        }

        if (moveNext && currentScreen.type !== 'Redirect') {
            const { responses } = this.state;
            responses[currentScreen.id] = response;
            this.setState({ responses }, () => this.changeScreen(response));
        }
        return moveNext;
    };

    doesFlowValueMatchResponse = (flowValue: string) => {
        let value: number | undefined, questionId: number;
        const currentScreen =
            this.props.questions[this.state.currentScreenIndex];
        let negate = false;

        try {
            if (flowValue.indexOf(':') >= 0) {
                if (flowValue.split(':')[1] === '*') return true;
                [questionId, value] = flowValue
                    .split(':')
                    .map((v: string, idx: number) => {
                        if (idx === 1 && v.startsWith('!')) {
                            negate = true;
                            return parseInt(v.substring(1), 10);
                        }
                        return parseInt(v, 10);
                    });
            } else {
                if (flowValue === '*') return true;
                if (flowValue.startsWith('!')) {
                    negate = true;
                    value = parseInt(flowValue.substring(1), 10);
                } else {
                    value = parseInt(flowValue, 10);
                }

                questionId = currentScreen.id;
            }

            if (value === -1) value = undefined;

            let resp = this.state.responses[questionId];
            if (!resp) return false;

            let val = resp && resp.options ? resp.options : [resp.value];
            return negate ? !val.includes(value) : val.includes(value);
        } catch (ex) {
            throw ex;
        }
    };

    changeScreen = (response: IResponse) => {
        const currentScreen =
            this.props.questions[this.state.currentScreenIndex];
        let nextIndex = this.state.currentScreenIndex + 1;

        if (currentScreen.flows) {
            let found = false;
            for (let flow of currentScreen.flows) {
                switch (flow.operator) {
                    case 'OR':
                        if (
                            flow.values.some((r) =>
                                this.doesFlowValueMatchResponse(r)
                            )
                        ) {
                            nextIndex =
                                this.props.questions.findIndex(
                                    (s) => s.id === flow.jump_to
                                ) || nextIndex;
                            found = true;
                        }
                        break;
                    case 'AND':
                        if (
                            flow.values.every((r) =>
                                this.doesFlowValueMatchResponse(r)
                            )
                        ) {
                            nextIndex =
                                this.props.questions.findIndex(
                                    (s) => s.id === flow.jump_to
                                ) || nextIndex;
                            found = true;
                        }
                        break;
                    default:
                }
                if (found) break;
            }
        }

        if (nextIndex === this.props.questions.length - 1) this.finishSurvey();

        if (nextIndex <= this.props.questions.length - 1) {
            const newScreens = [
                ...this.state.screensToDraw,
                this.props.questions[nextIndex],
            ];
            this.setState({
                currentScreenIndex: nextIndex,
                screensToDraw: newScreens,
            });
        }
    };

    finishSurvey = () => {
        if (this.props.onSurveyFinished) this.props.onSurveyFinished();
    };

    scrollEvent = () => {
        this.scrollToCurrentScreen(false);
    };

    scrollToCurrentScreen = (smooth = true) => {
        if (!this.props.questions || this.props.questions.length === 0) return;
        const itemId = this.props.questions[this.state.currentScreenIndex].id;

        const elem = document?.querySelector(`#screen-${itemId}`);
        if (elem) {
            elem.scrollIntoView({
                behavior: smooth ? 'smooth' : 'auto',
                block: 'start',
            });
            var newScreens = [...this.state.screensToDraw];
            if (newScreens.length > 1) newScreens.shift();
            setTimeout(() => {
                this.setState({ screensToDraw: newScreens });
            }, 500);
        } else console.warn('elemento no encontrado');
    };

    replaceTokens = (input: string, responses?: IResponses) => {
        const regex = /(?:%)(.*?)(?:%)/g;
        const { survey } = this.props;

        if (!survey || !survey.config || !survey.config.tokens)
            return input.replace(regex, '');

        const keys = input.match(regex) || [];
        let str = input;

        for (let key of keys) {
            let name = key.replace(/%/g, '');

            if (name.match(/^[0-9]+\.(value|text)$/)) {
                let qId = parseInt(name.split('.')[0], 10);
                let prop = name.split('.')[1] === 'value' ? 'meta' : 'text';
                str = str.replace(
                    new RegExp(key, 'gi'),
                    (responses ?? ({} as any))[qId][prop] ?? `%${name}%`
                );
            } else {
                let token = survey.config.tokens.find(
                    (t: any) => t.name === name
                );
                str = str.replace(
                    new RegExp(key, 'gi'),
                    token ? token.value : `%${name}%`
                );
            }
        }

        return str;
    };

    renderScreen = (screen: Question, index: number) => {
        const { responses, currentScreenIndex } = this.state;
        const Comp = screenTypes[screen.type] || screenTypes.Note;
        const isLocked =
            this.props.mode === 'Static' ||
            this.state.answeredQuestions.indexOf(screen.id) >= 0;

        return (
            <div
                key={screen.id}
                id={`screen-${screen.id}`}
                style={{ width: '100%' }}
            >
                <Comp
                    index={index}
                    isActive={
                        screen.id ===
                        this.props.questions[currentScreenIndex].id
                    }
                    data={screen}
                    responses={responses}
                    onNext={
                        this.props.mode !== 'Static'
                            ? this.onQuestionSubmit
                            : () => Promise.resolve(true)
                    }
                    locked={isLocked}
                    replaceTokens={this.replaceTokens}
                />
            </div>
        );
    };

    render() {
        return (
            <div
                className={`${styles.survey} qr-survey`}
                style={this.themeStyle}
            >
                <Helmet>
                    <meta
                        name="theme-color"
                        content={(this.themeStyle || {})['--primary-color']}
                    />
                </Helmet>
                {(this.state.screensToDraw || []).map((s, idx) =>
                    this.renderScreen(s, idx)
                )}
            </div>
        );
    }
}

export default QuestionsList;
